<template>
    <div class="page-container">
        <h2 class="page-title">地址管理</h2>

        <div class="search-bar">
            <el-input v-model="searchKeyword" placeholder="搜索收件人或手机号" clearable @change="handleSearch"
                style="width: 300px; margin-right: 15px;">
                <template #prefix>
                    <el-icon>
                        <search />
                    </el-icon>
                </template>
            </el-input>
        </div>

        <el-table :data="addressList" stripe style="width: 100%" v-loading="loading">
            <el-table-column prop="name" label="收件人" width="120" />
            <el-table-column prop="phone" label="手机号" width="130" />
            <!-- 修改后的地址显示列 -->
            <el-table-column label="所在地区">
                <template #default="{ row }">
                    {{ row.area?.join(' ') || '' }}
                </template>
            </el-table-column>
            <el-table-column prop="detail" label="详细地址" />
            <el-table-column label="默认地址" width="100">
                <template #default="{ row }">
                    <el-tag v-if="row.is_default" type="success">默认</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="操作" width="150">
                <template #default="{ row }">
                    <el-button size="small" @click="openEditDialog(row)">编辑</el-button>
                    <el-button size="small" type="danger" @click="handleDelete(row.id)">删除</el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
            <el-pagination v-model:current-page="pagination.currentPage" v-model:page-size="pagination.pageSize"
                :page-sizes="[10, 20, 50, 100]" :total="pagination.total"
                layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
                @current-change="handleCurrentChange" />
        </div>

        <!-- 编辑弹窗 -->
        <el-dialog :title="dialogTitle" v-model="dialogVisible" width="600px" :close-on-click-modal="false">
            <el-form :model="form" label-width="80px" ref="formRef" :rules="rules">
                <el-form-item label="收件人" prop="name">
                    <el-input v-model="form.name" />
                </el-form-item>
                <el-form-item label="手机号" prop="phone">
                    <el-input v-model="form.phone" />
                </el-form-item>
                <el-form-item label="所在地区" prop="area">
                    <el-input v-model="form.areaInput" placeholder="请输入省市区（用空格分隔），例如：广东省 广州市 天河区" />
                </el-form-item>
                <el-form-item label="详细地址" prop="detail">
                    <el-input v-model="form.detail" placeholder="街道、小区、门牌号等" type="textarea" :rows="2" />
                </el-form-item>
                <el-form-item label="默认地址">
                    <el-switch v-model="form.is_default" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitForm" :loading="submitting">保存</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios'
import config from '@/config'
import { Search } from '@element-plus/icons-vue'
import Cookies from 'js-cookie';
import { debounce } from 'lodash-es';

export default {
    name: 'UserAddress',
    components: {
        Search
    },
    data() {
        return {
            loading: false,
            submitting: false,
            addressList: [],
            dialogVisible: false,
            searchKeyword: '',
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            form: {
                id: null,
                store_id: null,
                name: '',
                phone: '',
                area: [],
                detail: '',
                is_default: false
            },
            areaOptions: [
                {
                    value: '广东省',
                    label: '广东省',
                    children: [
                        {
                            value: '广州市',
                            label: '广州市',
                            children: [
                                { value: '天河区', label: '天河区' },
                                { value: '越秀区', label: '越秀区' },
                                { value: '海珠区', label: '海珠区' }
                            ]
                        }
                    ]
                }
            ],
            rules: {
                name: [{ required: true, message: '请输入收件人姓名', trigger: 'blur' }],
                phone: [
                    { required: true, message: '请输入手机号', trigger: 'blur' },
                    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
                ],
                area: [{
                    required: true,
                    validator: (rule, value, callback) => {
                        const parts = this.form.areaInput?.trim().split(/\s+/) || []
                        if (parts.length !== 3) {
                            callback(new Error('请按格式输入完整的省市区'))
                        } else {
                            callback()
                        }
                    },
                    trigger: 'blur'
                }],
                detail: [{ required: true, message: '请输入详细地址', trigger: 'blur' }]
            }
        }
    },
    computed: {
        dialogTitle() {
            return '编辑地址'
        },
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.loadAddressList()
    },
    methods: {
        async loadAddressList() {
            try {
                this.loading = true
                const { data } = await axios.get(`${config.BASE_URL}/shop/api/user_address.php`, {
                    params: {
                        store_id: this.userInfo.id,
                        search: this.searchKeyword,
                        page: this.pagination.currentPage,
                        page_size: this.pagination.pageSize
                    }
                })

                if (data.code === 200) {
                    this.addressList = data.data.list.map(item => ({
                        ...item,
                        area: item.area || []
                    }))
                    this.pagination.total = data.data.total
                }
            } catch (error) {
                console.error('加载失败:', error)
                this.$message.error('数据加载失败')
            } finally {
                this.loading = false
            }
        },
        handleSearch: debounce(function () {
            this.pagination.currentPage = 1
            this.loadAddressList()
        }, 500),
        handleSizeChange(val) {
            this.pagination.pageSize = val
            this.loadAddressList()
        },
        handleCurrentChange(val) {
            this.pagination.currentPage = val
            this.loadAddressList()
        },
        openEditDialog(row) {
            this.$refs.formRef?.resetFields()
            this.form = {
                id: row.id,
                store_id: this.userInfo.id,
                name: row.name,
                phone: row.phone,
                areaInput: row.area?.join(' ') || '', // 数组转字符串

                detail: row.detail,
                is_default: !!row.is_default
            }
            this.dialogVisible = true
        },
        async submitForm() {
            try {
                const valid = await this.$refs.formRef.validate()
                if (!valid) return

                this.submitting = true

                // 手动转换地区格式
                this.form.area = this.form.areaInput
                    .trim()
                    .split(/\s+/) // 支持多个空格分隔
                    .slice(0, 3) // 确保只取前三个部分
                const payload = {
                    ...this.form,
                    is_default: this.form.is_default ? 1 : 0
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/user_address.php`,
                    payload
                )

                if (data.code === 200) {
                    this.$message.success('保存成功')
                    this.dialogVisible = false
                    this.loadAddressList()
                }
            } catch (error) {
                const msg = error.response?.data?.message || '保存失败'
                this.$message.error(msg)
            } finally {
                this.submitting = false
            }
        },
        async handleDelete(id) {
            try {
                await this.$confirm('确定删除该地址？删除后不可恢复', '警告', {
                    type: 'warning',
                    confirmButtonClass: 'el-button--danger'
                })

                const { data } = await axios.delete(
                    `${config.BASE_URL}/shop/api/user_address.php`,
                    { data: { id, store_id: this.userInfo.id } }
                )

                if (data.code === 200) {
                    this.$message.success('删除成功')
                    // 如果删除的是最后一页的最后一条，回到前一页
                    if (this.addressList.length === 1 && this.pagination.currentPage > 1) {
                        this.pagination.currentPage -= 1
                    }
                    this.loadAddressList()
                }
            } catch (error) {
                if (error !== 'cancel') {
                    const msg = error.response?.data?.message || '删除失败'
                    this.$message.error(msg)
                }
            }
        }
    }
}
</script>

<style scoped>
.page-title {
    margin-bottom: 20px;
    color: #333;
    font-weight: 600;
}

.search-bar {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.el-table {
    margin-top: 15px;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

:deep(.el-table__empty-block) {
    min-height: 200px;
}
</style>